<!--
  Companies among the user's picked contacts.

  The web picker hands over only what the user selects — the whole address book
  is not readable here, so unlike the native app this is "pick contacts to
  check", not a scan. The comparison itself runs entirely on this device,
  against the directory rows the client already holds: the usual way — asking a
  server which numbers it knows — would disclose exactly the list this design
  refuses to send.

  Rendered only where the Contact Picker API exists (Android Chromium); hidden
  everywhere else rather than shown broken.
-->
<script lang="ts">
  import { entriesForNumbers } from '$lib/directory/directory-repo';
  import { formatPhone } from '$lib/format';
  import { t } from '$lib/i18n/index.svelte';
  import { toE164 } from '$lib/protocol/registration';
  import { canSendTo, type DirectoryEntry } from '$lib/protocol/types';

  import Avatar from './Avatar.svelte';

  let { onpick }: { onpick: (phone: string) => void } = $props();

  let stage = $state<'idle' | 'checking' | 'done' | 'error'>('idle');
  let found = $state<DirectoryEntry[]>([]);

  async function pickAndCheck(): Promise<void> {
    let picked: { tel?: string[] }[];
    try {
      picked = (await navigator.contacts!.select(['tel'], { multiple: true })) ?? [];
    } catch {
      // A second picker already open, or the API refusing — soft note, the
      // number field stays available.
      stage = 'error';
      return;
    }
    if (picked.length === 0) {
      // Cancelled: back to the button, silently.
      stage = 'idle';
      return;
    }
    stage = 'checking';
    const numbers: string[] = [];
    for (const contact of picked) {
      for (const raw of contact.tel ?? []) {
        const phone = toE164(raw);
        if (phone !== undefined) {
          numbers.push(phone);
        }
      }
    }
    const entries = await entriesForNumbers(numbers);
    found = entries.filter((e) => canSendTo(e.status) && e.phone !== undefined);
    stage = 'done';
  }
</script>

{#if 'contacts' in navigator && 'ContactsManager' in window}
  <section>
    <h2>{t('add.contacts.title')}</h2>
    <p class="hint">{t('add.contacts.body')}</p>
    {#if stage === 'idle'}
      <button class="pick" onclick={() => void pickAndCheck()}>{t('add.contacts.pick')}</button>
    {:else if stage === 'checking'}
      <p class="hint">{t('add.contacts.checking')}</p>
    {:else if stage === 'error'}
      <p class="hint">{t('add.contacts.error')}</p>
    {:else if found.length === 0}
      <p class="hint">{t('add.contacts.none')}</p>
    {:else}
      <ul class="matches">
        <!-- Matches always carry the number: it is what this device hashed to
             find them. The trading name is not in the directory — it arrives
             from the company's own server after signing in. -->
        {#each found as entry (entry.entryKey)}
          <li>
            <button class="row" onclick={() => onpick(entry.phone!)}>
              <Avatar name="" phone={entry.phone!} />
              <span class="number">{formatPhone(entry.phone!)}</span>
              <span class="chevron" aria-hidden="true">›</span>
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </section>
{/if}

<style>
  section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  h2 {
    margin: 0;
    font-size: 18px;
  }
  .hint {
    margin: 0;
    font-size: 14px;
    color: var(--ink-soft);
  }
  .pick {
    align-self: flex-start;
    border: 1px solid var(--line);
    border-radius: 999px;
    background: var(--surface);
    color: var(--ink);
    padding: 10px 18px;
    font-size: 15px;
    cursor: pointer;
  }
  .matches {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: left;
    border: 0;
    background: transparent;
    padding: 8px 0;
    cursor: pointer;
    color: var(--ink);
    font: inherit;
  }
  .number {
    flex: 1;
    font-size: 16px;
  }
  .chevron {
    color: var(--ink-soft);
    font-size: 22px;
  }
</style>
